<template>
  <div class="ranking-tab">
    <div class="ranking-grid">
      <!-- LEFT: Configuration -->
      <div class="config-panel">
        <h3 class="section-title">{{ $t("rankingLayout_customization") }}</h3>

        <!-- Page Components -->
        <div class="config-section">
          <div class="config-section__header">
            {{ $t("rankingLayout_pageComponents") }}
          </div>

          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_SHOW_REGISTER"
            :label="registerLabel"
            class="cfg-check"
            hide-details
          />
          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_COUNTER"
            :label="timerLabel"
            class="cfg-check"
            hide-details
          />
          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_AWARD"
            :label="awardsLabel"
            class="cfg-check"
            hide-details
          />
          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_SPONSORED"
            :label="sponsorCard"
            class="cfg-check"
            hide-details
          />
        </div>

        <!-- Table Columns -->
        <div class="config-section">
          <div class="config-section__header">
            {{ $t("rankingLayout_tableColumns") }}
          </div>

          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_NAME"
            :label="participantName"
            class="cfg-check"
            hide-details
          />
          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_USERNAME"
            :label="username"
            class="cfg-check"
            hide-details
          />
          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER"
            :label="accountIdentifier"
            class="cfg-check"
            hide-details
          />
        </div>

        <!-- Privacy (SFB-1336): the backend masks the public ranking; only the preview is masked here -->
        <div class="config-section">
          <div class="config-section__header">
            {{ $t("rankingLayout_privacy") }}
          </div>
          <div class="config-section__hint">
            {{ $t("rankingLayout_privacyHint") }}
          </div>

          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_MASK_ACCOUNT_IDENTIFIER"
            :label="$t('rankingLayout_maskAccountIdentifier')"
            class="cfg-check"
            hide-details
          />
          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_MASK_PARTICIPANT_NAME"
            :label="$t('rankingLayout_maskParticipantName')"
            class="cfg-check"
            hide-details
          />
        </div>

        <!-- Actions & Navigation -->
        <div class="config-section">
          <div class="config-section__header">
            {{ $t("rankingLayout_actionsNavigation") }}
          </div>

          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_RULES"
            :label="rulesButton"
            class="cfg-check"
            hide-details
          />
          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_FAQS"
            :label="faqsButton"
            class="cfg-check"
            hide-details
          />
          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_FULL_PAGINATION"
            :label="tablePagination"
            class="cfg-check"
            hide-details
          />
        </div>

        <!-- Graph -->
        <div class="config-section">
          <div class="config-section__header">Graph</div>

          <v-checkbox
            v-model="selectedFeatures"
            value="TOURNAMENT_RANKING_PUBLIC_PERFORMANCE"
            :label="publicPerformance"
            class="cfg-check"
            hide-details
          />
        </div>

        <!-- Media Style -->
        <div class="config-section">
          <div class="config-section__header">
            {{ $t("rankingLayout_medalStyle") }}
          </div>

          <div class="medals-grid">
            <div
              v-for="opt in medalOptions"
              :key="opt.value"
              class="medal-card"
              :class="{ active: mediaStyle === opt.value }"
              role="button"
              tabindex="0"
              @click="mediaStyle = opt.value"
              @keydown.enter.prevent="mediaStyle = opt.value"
              @keydown.space.prevent="mediaStyle = opt.value"
            >
              <div class="medal-card__top">
                <span
                  class="medal-radio"
                  :class="{ checked: mediaStyle === opt.value }"
                ></span>
                <span class="medal-label">{{ opt.label }}</span>
              </div>

              <div class="medal-icon">
                <!-- Mostrar imagen de medalla 1 para preview en las opciones -->
                <img
                  v-if="opt.medals && opt.medals[1]"
                  :src="opt.medals[1]"
                  :alt="opt.label"
                />
                <span v-else class="medal-emoji">{{ opt.emoji }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: Live Preview -->
      <div
        class="preview-panel"
        :class="{ 'is-scrollable': isPublicPerformanceEnabled }"
      >
        <div class="preview-card">
          <!-- header (MOVIDO DENTRO DE LA CARD) -->
          <div class="preview-header">
            <div class="preview-title">
              {{ $t("rankingLayout_livePreview") }}
            </div>
            <div class="preview-subtitle">
              {{ $t("rankingLayout_descrition1") }}
            </div>
          </div>
          <!-- top bar -->
          <div class="preview-top">
            <div class="preview-top-left">
              <i class="fa-regular fa-chart-bar mr-2"></i>
              {{ $t("rankingLayout_customization") }}
            </div>
          </div>

          <!-- status / time row -->
          <div class="preview-meta">
            <span class="status-pill">{{
              $t("rankingLayout_buttonActive")
            }}</span>
            <span
              v-if="selectedFeatures.includes('TOURNAMENT_RANKING_COUNTER')"
              class="meta-right"
            >
              <i class="fa-regular fa-clock mr-1"></i> 14d 12h 30m
            </span>
          </div>

          <!-- title -->
          <div class="preview-name">
            {{ $t("rankingLayout_sampleTournamentName") }}
            <div class="preview-organizer">
              {{ $t("rankingLayout_sampleOrganizer") }}
            </div>
          </div>


          <div class="preview-body">
            <!-- LEFT: table + medals -->
            <div class="preview-main">
              <!-- Medals (only if awards card enabled - looks like mock) -->
              <!-- Medals (only if awards card enabled - looks like mock) -->
              <div
                v-if="selectedFeatures.includes('TOURNAMENT_RANKING_AWARD')"
                class="preview-section-card"
              >
                <div class="table-title-row">
                  {{ $t("rankingLayout_medals") }}
                </div>

                <div class="preview-medals">
                  <div class="medal" :class="`medal-${mediaStyleClass}`">
                    <img
                      v-if="medalImages[2]"
                      :src="medalImages[2]"
                      alt="2nd place medal"
                    />
                    <span v-else>🥈</span>
                  </div>

                  <div class="medal" :class="`medal-${mediaStyleClass}`">
                    <img
                      v-if="medalImages[1]"
                      :src="medalImages[1]"
                      alt="1st place medal"
                    />
                    <span v-else>🥇</span>
                  </div>

                  <div class="medal" :class="`medal-${mediaStyleClass}`">
                    <img
                      v-if="medalImages[3]"
                      :src="medalImages[3]"
                      alt="3rd place medal"
                    />
                    <span v-else>🥉</span>
                  </div>
                </div>
              </div>

              <!-- Tabs (Leaderboard / Participation) -->

              <!-- Table -->
              <div class="preview-table">
                <div class="table-title-row">
                  {{ $t("rankingLayout_leaderboard") }}
                </div>
                <div class="trow thead">
                  <span class="cell">{{ $t("rankingLayout_rank") }}</span>

                  <span
                    v-if="selectedFeatures.includes('TOURNAMENT_RANKING_NAME')"
                    class="cell"
                  >
                    {{ $t("rankingLayout_name") }}
                  </span>
                  <span
                    v-if="
                      selectedFeatures.includes('TOURNAMENT_RANKING_USERNAME')
                    "
                    class="cell"
                  >
                    {{ $t("rankingLayout_user") }}
                  </span>
                  <span
                    v-if="
                      selectedFeatures.includes(
                        'TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER',
                      )
                    "
                    class="cell cell--account"
                  >
                    Acc.
                  </span>

                  <span class="cell">{{ $t("rankingLayout_score") }}</span>
                </div>

                <div class="trow" v-for="n in 3" :key="n">
                  <span class="cell">{{ n }}</span>

                  <span
                    v-if="selectedFeatures.includes('TOURNAMENT_RANKING_NAME')"
                    class="cell"
                  >
                    {{ previewName(`Player ${n}`) }}
                  </span>
                  <span
                    v-if="
                      selectedFeatures.includes('TOURNAMENT_RANKING_USERNAME')
                    "
                    class="cell"
                  >
                    {{ previewName(`user${n}`) }}
                  </span>
                  <span
                    v-if="
                      selectedFeatures.includes(
                        'TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER',
                      )
                    "
                    class="cell cell--account"
                  >
                    {{ previewAccount(sampleAccounts[n - 1]) }}
                  </span>

                  <span class="cell">{{ (100 - n * 10).toFixed(2) }}%</span>
                </div>

                <div
                  v-if="
                    selectedFeatures.includes(
                      'TOURNAMENT_RANKING_FULL_PAGINATION',
                    )
                  "
                  class="tpagination"
                >
                  <button class="pbtn">‹</button>
                  <span class="ptext">1 2 3 4</span>
                  <button class="pbtn">›</button>
                </div>
              </div>
            </div>

            <!-- RIGHT: side cards -->
            <div class="preview-side">
              <div class="side-card">
                <div class="table-title-row">
                  {{ $t("rankingLayout_yourParticipation") }}
                </div>
                <div class="side-body">...</div>
              </div>

              <div
                v-if="selectedFeatures.includes('TOURNAMENT_RANKING_AWARD')"
                class="side-card"
              >
                <div class="table-title-row">
                  {{ $t("rankingLayout_awards") }}
                </div>
                <div class="side-body">
                  <i class="fa-regular fa-trophy"></i>
                  {{ $t("rankingLayout_sampleAwards1") }}
                </div>
              </div>

              <div
                v-if="selectedFeatures.includes('TOURNAMENT_RANKING_SPONSORED')"
                class="side-card"
              >
                <div class="table-title-row">
                  {{ $t("rankingLayout_sponsor") }}
                </div>
                <div class="side-body sponsor-box"></div>
              </div>

              <div
                v-if="
                  selectedFeatures.includes('TOURNAMENT_RANKING_RULES') ||
                  selectedFeatures.includes('TOURNAMENT_RANKING_FAQS')
                "
                class="side-actions"
              >
                <button
                  v-if="selectedFeatures.includes('TOURNAMENT_RANKING_RULES')"
                  class="action-btn"
                >
                  {{ $t("rankingLayout_viewRules") }}
                </button>
                <button
                  v-if="selectedFeatures.includes('TOURNAMENT_RANKING_FAQS')"
                  class="action-btn"
                >
                  {{ $t("rankingLayout_viewFAQs") }}
                </button>
              </div>
            </div>
          </div>
          <!-- Public performance chart -->
          <div
            v-if="
              selectedFeatures.includes('TOURNAMENT_RANKING_PUBLIC_PERFORMANCE')
            "
            class="preview-section-card"
          >
            <div class="table-title-row">
              {{ $t("publicPerformance") }}
            </div>
            <div class="preview-graph">
              <PerformanceChart
                :isDarkMode="isDarkPreview"
                :width="520"
                :height="180"
              />
            </div>
          </div>
        </div>
        <!-- Optional: contrast status like your notes -->
        <!--<div class="contrast-row">
          <span>Contrast standard Met</span>
          <div class="contrast-icons">
            <i class="fa-regular fa-circle-check"></i>
            <span>AA</span>
          </div>
        </div>-->
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import Repository from "@/repositories/tournament";
import PerformanceChart from "./PerformanceChart.vue";

export default {
  name: "RankingTab",
  components: {
    PerformanceChart,
  },
  props: {
    // si tu wizard lo usa, si no, deja default true para evitar que se oculte
    stepActive: { type: Boolean, default: true },
    setNextBtnDisabled: { type: Function, default: null },
  },
  data() {
    return {
      selectedFeatures: [],
      // Fictitious MT-style logins for the preview, so the account mask looks like the real ranking.
      sampleAccounts: ["205318476", "318904652", "472156830"],
      mediaStyle: "DEFAULT",
      initialValues: null,
      currentGraphSrc: null,
      medalOptions: [
        {
          label: "Default",
          value: "DEFAULT",
          medals: {
            1: require("@/assets/icons/default/Medal-1.png"),
            2: require("@/assets/icons/default/Medal-2.png"),
            3: require("@/assets/icons/default/Medal-3.png"),
          },
          emoji: "🏅",
        },
        {
          label: "3D",
          value: "THREE_D",
          medals: {
            1: require("@/assets/icons/3d/Medal-1.png"),
            2: require("@/assets/icons/3d/Medal-2.png"),
            3: require("@/assets/icons/3d/Medal-3.png"),
          },
          emoji: "🥇",
        },
        {
          label: "Neon",
          value: "NEON",
          medals: {
            1: require("@/assets/icons/neon/Medal-1.png"),
            2: require("@/assets/icons/neon/Medal-2.png"),
            3: require("@/assets/icons/neon/Medal-3.png"),
          },
          emoji: "✨",
        },
        {
          label: "Retro",
          value: "RETRO",
          medals: {
            1: require("@/assets/icons/retro/Medal-1.png"),
            2: require("@/assets/icons/retro/Medal-2.png"),
            3: require("@/assets/icons/retro/Medal-3.png"),
          },
          emoji: "🕹️",
        },
      ],
    };
  },
  computed: {
    isPublicPerformanceEnabled() {
      return this.selectedFeatures.includes(
        "TOURNAMENT_RANKING_PUBLIC_PERFORMANCE",
      );
    },
    tournamentSummary() {
      return this.$store.state.tournament;
    },
    mediaStyleClass() {
      // para estilos de “medals” o pequeños acentos visuales
      return (this.mediaStyle || "DEFAULT").toLowerCase();
    },
    currentMedalStyle() {
      const style = this.medalOptions.find(
        (opt) => opt.value === this.mediaStyle,
      );
      return style || this.medalOptions[0];
    },
    medalImages() {
      return this.currentMedalStyle.medals || {};
    },
    graphSrc() {
      // Use vuetify theme to detect dark mode reactively
      const isDark =
        this.$vuetify &&
        this.$vuetify.theme &&
        this.$vuetify.theme.dark === true;
      if (isDark) {
        return require("@/assets/graph-dark.jpg");
      }
      return require("@/assets/graph.png");
    },
    isDarkPreview() {
      return (
        this.$vuetify &&
        this.$vuetify.theme &&
        this.$vuetify.theme.dark === true
      );
    },
    awardsLabel() {
      return this.$t("rankingLayout_awardsCard");
    },
    registerLabel(){
      return this.$t("rankingLayout_registerLabel");
    },
    timerLabel() {
      return this.$t("rankingLayout_tournamentTimer");
    },
    sponsorCard() {
      return this.$t("rankingLayout_sponsorCard");
    },
    participantName() {
      return this.$t("rankingLayout_participantName");
    },
    username() {
      return this.$t("rankingLayout_username");
    },
    accountIdentifier() {
      return this.$t("rankingLayout_accountIdentifier");
    },
    rulesButton() {
      return this.$t("rankingLayout_rulesButton");
    },
    faqsButton() {
      return this.$t("rankingLayout_faqsButton");
    },
    tablePagination() {
      return this.$t("rankingLayout_tablePagination");
    },
    publicPerformance() {
      return this.$t("publicPerformance");
    },
  },
  mounted() {
    if (this.tournamentSummary) {
      this.init();
    }
    // Disable save button initially
    if (this.setNextBtnDisabled) {
      this.setNextBtnDisabled(true);
    }
  },
  watch: {
    tournamentSummary: {
      handler(newVal) {
        if (newVal && !this.initialValues) {
          this.init();
        }
      },
      immediate: true,
    },
    selectedFeatures: {
      handler() {
        this.checkForChanges();
      },
      deep: true,
    },
    mediaStyle() {
      this.checkForChanges();
    },
  },
  methods: {
    ...mapMutations(["setTournament"]),

    // Live preview only: mirrors the backend masks applied to the public ranking.
    previewName(value) {
      if (!this.selectedFeatures.includes("TOURNAMENT_RANKING_MASK_PARTICIPANT_NAME")) {
        return value;
      }
      return `${Array.from(value)[0] || ""}***`;
    },
    previewAccount(value) {
      if (!this.selectedFeatures.includes("TOURNAMENT_RANKING_MASK_ACCOUNT_IDENTIFIER")) {
        return value;
      }
      const chars = Array.from(value);
      const hidden = Math.max(chars.length - 5, 0);
      return "*".repeat(hidden) + chars.slice(hidden).join("");
    },

    updateGraphSrc() {
      const isDark =
        this.$vuetify &&
        this.$vuetify.theme &&
        this.$vuetify.theme.dark === true;
      this.currentGraphSrc = isDark
        ? require("@/assets/graph-dark.jpg")
        : require("@/assets/graph.png");
    },

    init() {
      if (!this.tournamentSummary) {
        return;
      }

      // Initialize selectedFeatures from store
      if (
        this.tournamentSummary.selectedFeatures &&
        Array.isArray(this.tournamentSummary.selectedFeatures)
      ) {
        this.selectedFeatures = [...this.tournamentSummary.selectedFeatures];
      } else {
        // If no selectedFeatures array, enable all features (backward compatibility)
        this.selectedFeatures = [
          "TOURNAMENT_RANKING_SHOW_REGISTER",
          "TOURNAMENT_RANKING_COUNTER",
          "TOURNAMENT_RANKING_AWARD",
          "TOURNAMENT_RANKING_SPONSORED",
          "TOURNAMENT_RANKING_NAME",
          "TOURNAMENT_RANKING_USERNAME",
          "TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER",
          "TOURNAMENT_RANKING_RULES",
          "TOURNAMENT_RANKING_FAQS",
          "TOURNAMENT_RANKING_FULL_PAGINATION",
        ];
      }

      // Initialize publicPerformance from tournamentSettings.publicPerformance
      // Add or remove TOURNAMENT_RANKING_PUBLIC_PERFORMANCE based on this value
      const publicPerformance =
        this.tournamentSummary.tournamentSettings?.publicPerformance;
      if (publicPerformance === true) {
        // Add TOURNAMENT_RANKING_PUBLIC_PERFORMANCE if not already present
        if (
          !this.selectedFeatures.includes(
            "TOURNAMENT_RANKING_PUBLIC_PERFORMANCE",
          )
        ) {
          this.selectedFeatures.push("TOURNAMENT_RANKING_PUBLIC_PERFORMANCE");
        }
      } else {
        // Remove TOURNAMENT_RANKING_PUBLIC_PERFORMANCE if present
        this.selectedFeatures = this.selectedFeatures.filter(
          (feature) => feature !== "TOURNAMENT_RANKING_PUBLIC_PERFORMANCE",
        );
      }

      // Initialize mediaStyle from tournamentSettings.medal_type
      // Map from backend values (default, 3d, neon, retro) to component values (DEFAULT, THREED, NEON, RETRO)
      const medalType = this.tournamentSummary.tournamentSettings?.medalType;
      console.log(this.tournamentSummary);
      if (medalType) {
        const medalTypeMap = {
          default: "DEFAULT",
          "3d": "THREE_D",
          neon: "NEON",
          retro: "RETRO",
        };
        this.mediaStyle = medalTypeMap[medalType.toLowerCase()] || "DEFAULT";
        console.log(medalTypeMap[medalType.toLowerCase()]);
        console.log(this.mediaStyle);
      }
      // Default is already "DEFAULT" in data()

      // Store initial values after initialization
      this.$nextTick(() => {
        this.saveInitialValues();
      });
    },

    saveInitialValues() {
      this.initialValues = {
        selectedFeatures: JSON.stringify([...this.selectedFeatures].sort()),
        mediaStyle: this.mediaStyle,
      };
    },

    checkForChanges() {
      if (!this.initialValues) {
        if (this.setNextBtnDisabled) {
          this.setNextBtnDisabled(true);
        }
        return;
      }

      const currentValues = {
        selectedFeatures: JSON.stringify([...this.selectedFeatures].sort()),
        mediaStyle: this.mediaStyle,
      };

      const hasChanges =
        currentValues.selectedFeatures !==
          this.initialValues.selectedFeatures ||
        currentValues.mediaStyle !== this.initialValues.mediaStyle;

      // Enable/disable save button based on changes
      if (this.setNextBtnDisabled) {
        this.setNextBtnDisabled(!hasChanges);
      }
    },
    saveTournamentSettings() {
      // Validate that tournament exists in store
      if (!this.tournamentSummary || !this.tournamentSummary.tournamentId) {
        this.$emit("save-error", {
          message:
            this.$t("alerts.errorOccurred") || "Tournament data not available",
        });
        return;
      }

      // Determine publicPerformance based on selectedFeatures
      const publicPerformance = this.selectedFeatures.includes(
        "TOURNAMENT_RANKING_PUBLIC_PERFORMANCE",
      );

      // Filter out TOURNAMENT_RANKING_PUBLIC_PERFORMANCE from selectedFeatures before sending
      const featuresToSend = this.selectedFeatures.filter(
        (feature) => feature !== "TOURNAMENT_RANKING_PUBLIC_PERFORMANCE",
      );

      // Map mediaStyle to medal_type for backend (DEFAULT -> default, THREED -> 3d, etc.)
      const mediaStyleToMedalType = {
        DEFAULT: "default",
        THREE_D: "3d",
        NEON: "neon",
        RETRO: "retro",
      };
      const medal_type = mediaStyleToMedalType[this.mediaStyle] || "default";

      const rankingData = {
        tournamentId: this.tournamentSummary.tournamentId,
        publicPerformance: publicPerformance,
        selectedFeatures: featuresToSend,
        medalType: medal_type,
      };

      Repository.editTournamentRanking(rankingData)
        .then((response) => {
          if (response.status < 400 && response.data) {
            // Update initial values after successful save
            this.saveInitialValues();

            // Disable save button after successful save
            if (this.setNextBtnDisabled) {
              this.setNextBtnDisabled(true);
            }

            // Refresh tournament data and update store
            Repository.getTournamentOverview(
              this.tournamentSummary.tournamentId,
            ).then((overviewResponse) => {
              if (overviewResponse.status < 400) {
                const updatedTournament = {
                  ...overviewResponse.data,
                };

                // Ensure tournamentSettings exists and update medal_type and publicPerformance
                if (!updatedTournament.tournamentSettings) {
                  updatedTournament.tournamentSettings = {};
                }

                // Map mediaStyle to medal_type for store
                const mediaStyleToMedalTypeMap = {
                  DEFAULT: "default",
                  THREE_D: "3d",
                  NEON: "neon",
                  RETRO: "retro",
                };

                updatedTournament.tournamentSettings.medalType =
                  mediaStyleToMedalTypeMap[this.mediaStyle] || "default";
                updatedTournament.tournamentSettings.publicPerformance =
                  publicPerformance;

                this.setTournament(updatedTournament);
              }
            });

            // Emit success event
            this.$emit("save-success", {
              message:
                this.$t("alerts_TournamentSettings.edited") ||
                "Ranking settings updated successfully",
            });
          } else {
            this.$emit("save-error", {
              message:
                this.$t("alerts.errorOccurred") ||
                "Error updating ranking settings",
            });
          }
        })
        .catch((error) => {
          this.$emit("save-error", {
            message:
              error.response?.data?.message ||
              this.$t("alerts.errorOccurred") ||
              "Error updating ranking settings",
          });
        });
    },
  },
};
</script>

<style scoped lang="scss">
.ranking-tab {
  width: 100%;
  font-family: "Avenir", sans-serif;
}

.ranking-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 22px;
  align-items: stretch;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 14px;
  color: var(--v-textPrimary-base);
}

.theme--dark .section-title {
  color: var(--v-fullWhite-base) !important;
}


.theme--dark .config-panel {
  background: var(--v-cardBackground-base) !important;
  border-color: #424242 !important;
}

.config-block {
  margin-bottom: 14px;
}

.block-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--v-textSecondary-base);
  margin-bottom: 8px;
}

.theme--dark .block-title {
  color: var(--v-mainWhite-base) !important;
}

.feature-checkbox {
  margin: 0;
  padding: 0;
}

.media-group {
  margin-top: 6px;
}

.preview-panel {
  border-left: none;
  padding-left: 0;
  padding-top: 0;
  margin-top: 0;
  overflow: visible;
}
.preview-panel.is-scrollable {
  max-height: calc(100vh - 10px); 
  overflow-y: auto;
  padding-right: 6px; 
}

.theme--dark .preview-panel {
  border-left-color: #424242;
}

.preview-header {
  text-align: center;
  margin: 0; /* clave: quita el empuje hacia abajo */
  padding: 18px 18px 12px; /* padding similar al config-panel */
  border-bottom: 1px solid #e6e6e6;
}

.theme--dark .preview-header {
  border-bottom-color: #424242;
}

.preview-title {
  font-family: "Aeonik", "Avenir", sans-serif !important;
  font-size: 30px;
  font-weight: 800;
  color: var(--v-textPrimary-base);
}

.theme--dark .preview-title {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
  -webkit-text-fill-color: var(--v-fullWhite-base) !important;
}

.preview-subtitle {
  font-size: 12px;
  color: var(--v-textSecondary-base);
}

.theme--dark .preview-subtitle {
  color: rgba(255, 255, 255, 0.8) !important;
}

.preview-card {
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.theme--dark .preview-card {
  background: var(--v-cardBackground-base) !important;
  border-color: #424242 !important;
}

.preview-top {
  padding: 10px 12px;
  border-bottom: 1px solid #ededed;
  font-weight: 600;
  font-size: 12px;
  color: #3c3c3c;
  display: flex;
  justify-content: space-between;
}

.theme--dark .preview-top {
  border-bottom-color: #424242;
  color: var(--v-fullWhite-base) !important;
}

.theme--dark .preview-top * {
  color: var(--v-fullWhite-base) !important;
}

.theme--dark .preview-top i {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
}

.preview-meta {
  display: flex;
  justify-content: space-between;
  padding: 10px 12px 0;
  font-size: 11px;
  color: #6a6a6a;
}

.status-pill {
  background: var(--v-buttonPrimary-base) !important;
  color: #fff;
  padding: 3px 8px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 10px;
}

.preview-name {
  padding: 10px 12px;
  font-weight: 800;
  font-size: 14px;
  color: #1f1f1f;
}

.theme--dark .preview-name {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
  -webkit-text-fill-color: var(--v-fullWhite-base) !important;
}

.preview-organizer {
  font-size: 11px;
  font-weight: 500;
  color: #777;
  margin-top: 2px;
}

.theme--dark .preview-organizer {
  color: rgba(255, 255, 255, 0.7) !important;
}

.preview-graph-section {
  padding: 0 12px 10px;
}

.preview-graph {
  padding: 8px 8px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.graph-image {
  width: 100%;
  height: auto;
  max-width: 100%;
  max-height: 120px;
  object-fit: contain;
}

.preview-body {
  display: grid;
  grid-template-columns: 1.4fr 0.9fr;
  gap: 12px;
  padding: 12px;
  background: #fafafa;
}

.theme--dark .preview-body {
  background: var(--v-cardBackground-base) !important;
}

.preview-main {
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 10px;
  padding: 10px;
}

.theme--dark .preview-main {
  background: var(--v-cardBackground-base);
  border-color: #424242;
}

.preview-medals {
  display: flex;
  gap: 12px;
  justify-content: space-around;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px 0;
  width: 100%;
}

.medal {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;

  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.3) 0%,
      transparent 100%
    );
    pointer-events: none;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8px;
    position: relative;
    z-index: 1;
  }

  span {
    font-size: 40px;
    position: relative;
    z-index: 1;
  }
}

/* ---------- MEDALS CARD (como en screenshot) ---------- */
.preview-section-card {
  border: 1px solid #dcdcdc;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin-bottom: 16px; /* separa de la tabla */
  margin-right: 12px;
  margin-left: 12px;
}

.theme--dark .preview-section-card {
  border-color: #424242;
  background: var(--v-cardBackground-base);
}

.preview-medals {
  width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
}

.table-title-row {
  background: #f3f3f3;
  border-bottom: 1px solid #ededed;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 700;
  color: #3a3a3a;
  text-align: center;
}

.theme--dark .table-title-row {
  background: #2a2a2a;
  border-bottom-color: #424242;
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
  -webkit-text-fill-color: var(--v-fullWhite-base) !important;
}

.theme--dark .table-title-row * {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
}

.preview-table {
  border: 1px solid #ededed;
  border-radius: 10px;
  overflow: hidden;
}

.theme--dark .preview-table {
  border-color: #424242;
}

.trow {
  display: flex;
  border-bottom: 1px solid #f2f2f2;
}

.theme--dark .trow {
  border-bottom-color: #424242;
}

.trow:last-child {
  border-bottom: 0;
}

.thead {
  background: #f7f7f7;
  font-weight: 700;
}

.theme--dark .thead {
  background: #2a2a2a;
}

.cell {
  flex: 1;
  padding: 7px 8px;
  font-size: 10px;
  text-align: center;
  color: #4b4b4b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 9-digit logins: wide enough that the last 5 digits stay visible when masked (****18476) */
.cell--account {
  flex: 1.8;
  padding-left: 4px;
  padding-right: 4px;
}

.theme--dark .cell {
  color: var(--v-mainWhite-base) !important;
}

.tpagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  padding: 8px;
  background: #f7f7f7;
  border-top: 1px solid #ededed;
}

.theme--dark .tpagination {
  background: #2a2a2a;
  border-top-color: #424242;
}

.pbtn {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 22px;
  font-size: 16px;
  vertical-align: middle;
}

.theme--dark .pbtn {
  border-color: #424242;
  background: var(--v-cardBackground-base);
}

.ptext {
  font-size: 10px;
  color: #666;

  display: inline-flex;
  align-items: center;
  line-height: 22px;
}

.theme--dark .ptext {
  color: var(--v-mainWhite-base) !important;
}

.preview-side {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.side-card {
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 10px;
  overflow: hidden;
}

.theme--dark .side-card {
  background: var(--v-cardBackground-base);
  border-color: #424242;
}
.side-title {
  background: #f7f7f7;
  border-bottom: 1px solid #ededed;
  font-size: 10px;
  font-weight: 800;
  padding: 8px;
}

.theme--dark .side-title {
  background: #2a2a2a;
  border-bottom-color: #424242;
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
  -webkit-text-fill-color: var(--v-fullWhite-base) !important;
}

.theme--dark .side-title * {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
}
.side-body {
  padding: 10px 8px;
  font-size: 10px;
  color: #777;
  min-height: 30px;
}
.sponsor-box {
  height: 46px;
  border-radius: 8px;
  border: 1px dashed #dcdcdc;
  background: #fafafa;
}

.theme--dark .sponsor-box {
  background: var(--v-cardBackground-base) !important;
  border-color: #424242 !important;
}

.side-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.action-btn {
  border: 1px solid #ededed;
  background: #fff;
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 10px;
  color: var(--v-mainBlack-base);
}

.theme--dark .action-btn {
  background: var(--v-cardBackground-base) !important;
  border-color: #424242 !important;
  color: var(--v-fullWhite-base) !important;
}

.contrast-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  font-size: 11px;
  color: #777;
}
.contrast-icons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.medals-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 14px;
  margin-top: 10px;
}

.medal-card {
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  background: #ffffff;
  padding: 12px;
  cursor: pointer;
  min-height: 110px;
  transition: 0.15s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:hover {
    border-color: #cfcfcf;
    transform: translateY(-1px);
  }

  &.active {
    border: 2px solid var(--v-mainGreen-base);
    box-shadow: 0 4px 14px rgba(0, 159, 99, 0.12);
  }
}

.theme--dark .medal-card {
  border-color: #424242;
  background: var(--v-cardBackground-base);
}

.medal-card__top {
  display: flex;
  align-items: center;
  gap: 10px;
}

.medal-label {
  font-family: "Avenir", sans-serif;
  font-size: 13px;
  color: #3a3a3a;
}

.theme--dark .medal-label {
  color: var(--v-mainWhite-base) !important;
}

.medal-radio {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #bdbdbd;
  display: inline-block;
  position: relative;
  flex: 0 0 auto;

  &.checked {
    border-color: var(--v-mainGreen-base);
  }

  &.checked::after {
    content: "";
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: var(--v-mainGreen-base);
  }
}

.medal-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 8px;

  img {
    width: 52px;
    height: 52px;
    object-fit: contain;
  }
}

.medal-emoji {
  font-size: 46px;
  line-height: 1;
}

@media (max-width: 960px) {
  .medals-grid {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }
}

/* LEFT PANEL look like screenshot */
.config-panel {
  background: #fff;
  border: 1px solid #ededed;
  border-radius: 10px;
  padding: 16px 14px;
}

.theme--dark .config-panel {
  background: var(--v-cardBackground-base) !important;
  border-color: #424242 !important;
}

.config-title {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #2f2f2f;
  padding: 4px 2px 12px;
}

.theme--dark .config-title {
  color: var(--v-fullWhite-base) !important;
}

/* each section block */
.config-section {
  padding: 12px 2px;
  border-top: 1px solid #f1f1f1;
}

.theme--dark .config-section {
  border-top-color: #424242;
}

.config-section:first-of-type {
  border-top: 0;
  padding-top: 6px;
}

.config-section__header {
  font-size: 13px;
  font-weight: 700;
  color: #14161b;
  margin-bottom: 8px;
  letter-spacing: 0.2px;
}

.theme--dark .config-section__header {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
}

.config-section__hint {
  font-size: 12px;
  line-height: 1.4;
  color: #666;
  margin: -4px 0 8px;
}

.theme--dark .config-section__hint {
  color: #999;
}

/* compact checkbox rows */
.cfg-check {
  margin: 0;
  padding: 0;
}

/* Vuetify checkbox internals (Vuetify 2) */
::v-deep .cfg-check .v-input__control {
  min-height: 22px;
}

::v-deep .cfg-check .v-input--selection-controls__input {
  margin-right: 10px;
}

/* label style */
::v-deep .cfg-check .v-label {
  font-size: 13px;
  line-height: 1.1;
}

.theme--dark ::v-deep .cfg-check .v-label {
  color: var(--v-fullWhite-base) !important;
  opacity: 1 !important;
  -webkit-text-fill-color: var(--v-fullWhite-base) !important;
}

/* tighter spacing between rows */
::v-deep .cfg-check .v-input__slot {
  margin-bottom: 6px;
}

/* make the checkbox color green like screenshot */
::v-deep .cfg-check .v-icon {
  font-size: 18px;
}

/* optional: slightly soften unchecked */
::v-deep .cfg-check .v-icon.notranslate.mdi-checkbox-blank-outline {
  opacity: 0.45;
}

.theme--light .ranking-tab {
  --disabled-bg: #f5f5f5;
  --disabled-border: #e0e0e0;
  --disabled-text: #9e9e9e;
  --disabled-icon: #9e9e9e;
}

.theme--dark .ranking-tab {
  --disabled-bg: rgba(255, 255, 255, 0.06);
  --disabled-border: rgba(255, 255, 255, 0.14);
  --disabled-text: rgba(255, 255, 255, 0.55);
  --disabled-icon: rgba(255, 255, 255, 0.45);
}

::v-deep .v-input--is-disabled .v-input__control .v-input__slot {
  background-color: var(--disabled-bg) !important;
  border-color: var(--disabled-border) !important;
}

::v-deep .v-input--is-disabled input,
::v-deep .v-input--is-disabled textarea {
  color: var(--disabled-text) !important;
  -webkit-text-fill-color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-label {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-select__selection {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-icon {
  color: var(--disabled-icon) !important;
}

::v-deep
  .v-input--is-disabled
  .v-input__details
  .v-messages
  .v-messages__message {
  color: var(--disabled-text) !important;
}

::v-deep .v-input--is-disabled .v-input__details .v-counter {
  color: var(--disabled-text) !important;
}
</style>
